/* eslint-disable react/prop-types */
import React, { Fragment, useContext, useCallback, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import TypeaheadSuggestionItem from './TypeaheadSuggestionItem';
import SuggestionsContainer from './TypeaheadSuggestionsContainer';
import SuggestionsAnnouncer from './TypeaheadSuggestionsAnnouncer';
import { forgeClassHelper } from '../utils/classes';
import { FormFieldLayoutContext } from '../FormFieldLayout/FormFieldLayoutContext';
import SuggestionSectionTitle from './TypeaheadSuggestionSectionTitle';

const classes = forgeClassHelper('typeahead');

function TypeaheadTemplate({
  suggestions,
  onSuggestionsFetchRequested,
  onSuggestionsClearRequested,
  getSuggestionValue,
  inputProps,
  renderInputComponent,
  onSuggestionSelected,
  shouldRenderSuggestions,
  className,
  suggestionsAnnouncement,
  renderSuggestion: renderSuggestionProp,
  multiSection,
  renderSectionTitle: renderSectionTitleProp,
  getSectionSuggestions,
  // other props from react-autosuggest that aren't used or explicitly called out in our API
  onSuggestionHighlighted,
  alwaysRenderSuggestions,
  highlightFirstSuggestion,
  focusInputOnSuggestionClick,
  getSuggestionText,
  id,
}) {
  /* see https://floating-ui.com/docs/react#external-elements for anchor */
  const [anchor, setAnchor] = useState(null);
  const renderSuggestionsContainer = useCallback(
    (props) =>
      anchor ? (
        <SuggestionsContainer {...props} showingSuggestions={suggestions.length > 0} anchor={anchor} />
      ) : undefined,
    [anchor]
  );

  const renderSuggestion = React.useCallback(
    (suggestion) => {
      const SuggestionComponent = renderSuggestionProp || TypeaheadSuggestionItem;
      return (
        <SuggestionComponent
          suggestion={suggestion}
          searchText={inputProps && inputProps.value}
          getSuggestionText={getSuggestionText}
        />
      );
    },
    [inputProps, getSuggestionText]
  );

  const renderSectionTitle = React.useCallback(
    ({ title }) => {
      const SectionTitleComponent = renderSectionTitleProp ?? SuggestionSectionTitle;
      return <SectionTitleComponent title={title} searchText={inputProps && inputProps.value} />;
    },
    [inputProps]
  );

  const context = useContext(FormFieldLayoutContext);
  const forgeTheme = {
    container: classes().className,
    containerOpen: 'fe_is-open',
    input: classes('input').className,
    inputOpen: 'fe_is-open',
    inputFocused: 'fe_is-focused',
    suggestionsContainer: classes('suggestions-container').className,
    suggestionsContainerOpen: 'fe_is-open',
    suggestionsList: classes('suggestions-list').className,
    suggestion: classes({ element: 'suggestion', modifiers: ['highlight', context ? context.layout : ''] }).className,
    suggestionFirst: classes('suggestion--first').className,
    suggestionHighlighted: 'fe_is-highlighted',
    sectionContainer: classes('section-container').className,
    sectionContainerFirst: classes('section-container--first').className,
    sectionTitle: classes('section-title').className,
  };

  const theme = className ? { ...forgeTheme, container: classes({ extra: className }).className } : forgeTheme;

  return (
    <Fragment>
      <Autosuggest
        // setting the reference (or anchor) element that is being referred to for positioning of SuggestionsContainer
        ref={setAnchor}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        inputProps={inputProps}
        renderInputComponent={renderInputComponent}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={shouldRenderSuggestions}
        className={className}
        // other props from react-autosuggest that aren't used or explicitly called out in our API
        onSuggestionHighlighted={onSuggestionHighlighted}
        alwaysRenderSuggestions={alwaysRenderSuggestions}
        highlightFirstSuggestion={highlightFirstSuggestion}
        focusInputOnSuggestionClick={focusInputOnSuggestionClick}
        multiSection={multiSection}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        theme={theme}
        id={id}
      />
      <SuggestionsAnnouncer text={suggestionsAnnouncement} />
    </Fragment>
  );
}

TypeaheadTemplate.defaultProps = {
  getSectionSuggestions(section) {
    return section.suggestions;
  },
};

export default TypeaheadTemplate;
